import type { LucideIcon } from 'lucide-react'
import { Bell, LogOut, PanelLeftClose, PanelLeftOpen, UserCircle2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { MilliswiftLogo } from './brand/MilliswiftLogo'
import { UserBadge, UserMenuHeader } from './domain'
import { IconButton, Kbd } from './primitives'
import { DropdownMenu, Tooltip } from './overlay'
import { cn } from './lib/cn'
import { useIsMobile, useLocalStorage } from './lib/hooks'

export interface NavItem {
  label: string
  href: string
  icon: LucideIcon
  /** Render as active for nested routes too (e.g. /tables/orders). */
  matchPrefix?: boolean
}

export interface ShellUser {
  name?: string | null
  email?: string | null
  avatarUrl?: string | null
}

export interface AppShellProps {
  /**
   * `full`    — the product console. Header carries an account badge whose
   *             menu links out to account management.
   * `account` — the account app itself. Same shell, but the user menu drops
   *             the now-redundant "Manage Account" entry, since that is where
   *             you already are.
   */
  variant: 'full' | 'account'
  nav: NavItem[]
  user?: ShellUser
  onSignOut: () => void
  /** Only used by the `full` variant. */
  accountUrl?: string
  /** Header content between the logo and the right-hand cluster, e.g. a database selector. */
  headerSlot?: React.ReactNode
  /** Notification count; renders a dot on the bell when greater than zero. */
  notificationCount?: number
  onNotificationsClick?: () => void
  onSearchClick?: () => void
  /** Rendered after the main content panel. */
  children: React.ReactNode
}

/**
 * The application shell shared by the account and console apps: a translucent
 * glass header, a collapsible icon sidebar, and a rounded content panel.
 *
 * The header is dynamic per variant rather than duplicated — one component,
 * two behaviours — so the two apps cannot drift apart.
 */
export function AppShell({
  variant,
  nav,
  user,
  onSignOut,
  accountUrl,
  headerSlot,
  notificationCount = 0,
  onNotificationsClick,
  onSearchClick,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useLocalStorage('ms-sidebar-collapsed', false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const isMobile = useIsMobile()
  const location = useLocation()

  // Navigating should always dismiss the drawer.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  const toggleSidebar = () => {
    if (isMobile) setMobileOpen((open) => !open)
    else setCollapsed((value) => !value)
  }

  // On mobile the drawer is either open or shut; on desktop it is wide or narrow.
  const sidebarOpen = isMobile ? mobileOpen : !collapsed

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-background">
      {/* ---------------- Header ---------------- */}
      <header
        className={cn(
          'relative z-50 flex h-[72px] shrink-0 items-center justify-between gap-4',
          'border-b border-line bg-surface/70 px-4 backdrop-blur-panel sm:px-6',
          // The signature metallic hairline, the one place it appears in chrome.
          'after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-silver after:opacity-40'
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <IconButton
            variant="ghost"
            size="sm"
            label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            onClick={toggleSidebar}
            icon={
              sidebarOpen ? (
                <PanelLeftClose className="h-[18px] w-[18px]" />
              ) : (
                <PanelLeftOpen className="h-[18px] w-[18px]" />
              )
            }
          />

          {/* The full wordmark needs room; the mark carries the header when narrow. */}
          <NavLink to={nav[0]?.href ?? '/'} className="flex items-center">
            <MilliswiftLogo
              variant={!isMobile && collapsed ? 'mark' : 'wordmark'}
              height={26}
              className="hidden sm:block"
            />
            <MilliswiftLogo variant="mark" height={26} className="sm:hidden" />
          </NavLink>

          {headerSlot ? <div className="ml-2 hidden min-w-0 lg:block">{headerSlot}</div> : null}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {onSearchClick ? (
            <button
              type="button"
              onClick={onSearchClick}
              className={cn(
                'hidden h-9 items-center gap-2 rounded-control border border-line bg-sunken/60 px-3 md:inline-flex',
                'text-caption text-subtle transition-colors hover:border-line-strong hover:text-muted'
              )}
            >
              <span>Search…</span>
              <Kbd>⌘K</Kbd>
            </button>
          ) : null}

          {onNotificationsClick ? (
            <IconButton
              variant="ghost"
              size="sm"
              label={
                notificationCount > 0
                  ? `Notifications, ${notificationCount} unread`
                  : 'Notifications'
              }
              onClick={onNotificationsClick}
              icon={
                <span className="relative">
                  <Bell className="h-[18px] w-[18px]" />
                  {notificationCount > 0 ? (
                    <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-danger ring-2 ring-surface" />
                  ) : null}
                </span>
              }
            />
          ) : null}

          <div className="ml-1 flex items-center border-l border-line pl-2">
            <DropdownMenu
              align="end"
              header={<UserMenuHeader user={user} />}
              items={[
                ...(variant === 'full' && accountUrl
                  ? [
                      {
                        id: 'account',
                        label: 'Manage Account',
                        icon: <UserCircle2 />,
                        onSelect: () => {
                          window.location.href = accountUrl
                        },
                      },
                    ]
                  : []),
                {
                  id: 'signout',
                  label: 'Sign out',
                  icon: <LogOut />,
                  danger: true,
                  separated: variant === 'full' && Boolean(accountUrl),
                  onSelect: onSignOut,
                },
              ]}
              trigger={
                <button
                  type="button"
                  aria-label="Account menu"
                  className="rounded-xl p-1 transition-colors hover:bg-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/50"
                >
                  <UserBadge user={user} showName={!isMobile} />
                </button>
              }
            />
          </div>
        </div>
      </header>

      {/* ---------------- Body ---------------- */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {/* Drawer backdrop, mobile only */}
        {isMobile && mobileOpen ? (
          <div
            className="absolute inset-0 z-30 animate-fade-in bg-overlay/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
        ) : null}

        {/* ---------------- Sidebar ---------------- */}
        <aside
          aria-label="Main navigation"
          className={cn(
            'z-40 flex flex-col border-r border-line bg-surface',
            'transition-[width,transform] duration-300 ease-brand',
            // Mobile: an off-canvas drawer
            'absolute inset-y-0 left-0 w-64',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
            // Desktop: participates in the layout and sizes with the toggle.
            // These must stay literal — Tailwind scans source text and cannot
            // see a class name assembled from a variable.
            'md:relative md:inset-auto md:translate-x-0',
            collapsed ? 'md:w-20' : 'md:w-64'
          )}
        >
          <nav className="ms-scroll min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-4">
            {nav.map((item) => {
              const Icon = item.icon

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={!item.matchPrefix}
                  title={!sidebarOpen ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center rounded-xl transition-colors duration-200',
                      sidebarOpen ? 'gap-3 px-3 py-2.5' : 'justify-center p-2.5',
                      isActive
                        ? 'bg-accent-tint/20 font-semibold text-foreground'
                        : 'text-muted hover:bg-sunken hover:text-foreground'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Left accent bar marks the active route. */}
                      {isActive ? (
                        <span
                          aria-hidden
                          className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-accent"
                        />
                      ) : null}

                      <Icon
                        className={cn(
                          'h-5 w-5 shrink-0 transition-colors',
                          isActive ? 'text-accent' : 'text-subtle group-hover:text-muted'
                        )}
                      />

                      {sidebarOpen ? (
                        <span className="flex-1 truncate text-left text-[13px] font-bold uppercase tracking-widest">
                          {item.label}
                        </span>
                      ) : (
                        <span className="sr-only">{item.label}</span>
                      )}
                    </>
                  )}
                </NavLink>
              )
            })}
          </nav>

          <div className="shrink-0 border-t border-line p-2">
            <button
              type="button"
              onClick={onSignOut}
              title={!sidebarOpen ? 'Sign out' : undefined}
              className={cn(
                'flex w-full items-center rounded-xl text-muted transition-colors',
                'hover:bg-danger-soft hover:text-danger-text',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/40',
                sidebarOpen ? 'gap-3 px-3 py-2.5' : 'justify-center p-2.5'
              )}
            >
              <LogOut className="h-5 w-5 shrink-0 text-subtle" />
              {sidebarOpen ? (
                <span className="flex-1 text-left text-[13px] font-bold uppercase tracking-widest">
                  Sign out
                </span>
              ) : (
                <span className="sr-only">Sign out</span>
              )}
            </button>
          </div>
        </aside>

        {/* ---------------- Content ---------------- */}
        <main className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-surface/50 backdrop-blur-sm">
          <div className="ms-scroll flex-1 overflow-y-auto p-6 sm:p-8 lg:p-10">{children}</div>
        </main>
      </div>
    </div>
  )
}

/* ==========================================================================
 * Sidebar toggle for shells that are not AppShell
 * ======================================================================== */

export interface SidebarToggleProps {
  open: boolean
  onToggle: () => void
  className?: string
}

export function SidebarToggle({ open, onToggle, className }: SidebarToggleProps) {
  return (
    <Tooltip content={open ? 'Collapse sidebar' : 'Expand sidebar'}>
      <IconButton
        variant="ghost"
        size="sm"
        label={open ? 'Collapse sidebar' : 'Expand sidebar'}
        onClick={onToggle}
        className={className}
        icon={
          open ? (
            <PanelLeftClose className="h-[18px] w-[18px]" />
          ) : (
            <PanelLeftOpen className="h-[18px] w-[18px]" />
          )
        }
      />
    </Tooltip>
  )
}
